import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getActiveSite, setActiveSite } from '../../bundles/sites';
import config from '../../config';

const VULNERABILITY_DISCLOSURE_POLICY_URL =
  'https://www.hitachienergy.com/products-and-solutions/cybersecurity/vulnerability-disclosure-policy';

// Strictly necessary cookies set by the authentication service (Keycloak).
const ESSENTIAL_COOKIES = [
  {
    name: 'AUTH_SESSION_ID',
    purpose: 'Tracks an in-progress login so the authentication flow can be completed.',
  },
  {
    name: 'KC_AUTH_SESSION_HASH',
    purpose:
      'Holds a hash of the login session ID so the login page and sign-in scripts can detect changes to the login session.',
  },
  {
    name: 'KC_RESTART',
    purpose: 'Lets the login flow be restarted if it is interrupted.',
  },
  {
    name: 'KEYCLOAK_IDENTITY',
    purpose: 'Identifies the authenticated user and keeps them signed in.',
  },
  {
    name: 'KEYCLOAK_SESSION',
    purpose: 'Tracks the user session so single sign-on and sign-out work correctly.',
  },
];

// Set by Autodesk's viewer CDN (developer.api.autodesk.com) when the 3D viewer loads.
const THIRD_PARTY_COOKIES = [
  {
    name: 'viewer-version-in-cookie',
    purpose:
      'Set by Autodesk when the 3D viewer loads. Contains the viewer version number. Expires after 48 hours.',
  },
];

const CookieTable = ({ cookies, label }) => (
  <table
    className="cookie-table"
    aria-label={label}
  >
    <thead>
      <tr>
        <th>Name</th>
        <th>Purpose</th>
      </tr>
    </thead>
    <tbody>
      {cookies.map(({ name, purpose }) => (
        <tr key={name}>
          <td>{name}</td>
          <td>{purpose}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const SupportView = ({
  match: {
    params: { id: siteId },
  },
}) => {
  const dispatch = useDispatch();
  const site = useSelector(getActiveSite);
  const { support } = site;

  useEffect(() => {
    if (siteId) {
      dispatch(setActiveSite(siteId));
    }
  }, [siteId]);

  return (
    <>
      {support ? (
        <div className="support-content">
          <div className="support-badge">
            <p>
              <strong>Serving customers. Supporting innovation.</strong>
            </p>
            <p>
              Hitachi Energy is committed to supplying support solutions that meet our customers
              needs and specifications. To rectify a problem as soon as possible, we provide several
              support options.
            </p>
            <p>
              Short response time to a knowledgeable Hitachi Energy engineer provides immediate
              access to solutions, saving valuable time. Minor incidents can be resolved quickly,
              and in the event of a major issue, immediate support is available.
            </p>
          </div>
          <div className="support-badge">
            <strong>Contact information</strong>
            <div>Name: {support.name || 'n/a'}</div>
            <div>
              Phone: {support.phone ? <a href={`tel:${support.phone}`}>{support.phone}</a> : 'n/a'}
            </div>
            <div>
              Email:{' '}
              {support.email ? <a href={`mailto:${support.email}`}>{support.email}</a> : 'n/a'}
            </div>
          </div>
          <div className="support-badge">
            <strong>Essential cookies</strong>
            <p>The following essential cookies are required for sign-in and cannot be disabled.</p>
            <CookieTable
              cookies={ESSENTIAL_COOKIES}
              label="Essential cookies"
            />
          </div>
          {!config.onPremise && (
            <div className="support-badge">
              <strong>Third-party functional cookies</strong>
              <p>
                The 3D model viewer is provided by Autodesk and loaded from their servers. Autodesk
                sets the following cookie on its own domain whenever the viewer is loaded.
              </p>
              <CookieTable
                cookies={THIRD_PARTY_COOKIES}
                label="Third-party functional cookies"
              />
            </div>
          )}
          <div className="support-badge">
            <strong>Vulnerability disclosure policy</strong>
            <p>
              <a
                href={VULNERABILITY_DISCLOSURE_POLICY_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                {VULNERABILITY_DISCLOSURE_POLICY_URL}
              </a>
            </p>
          </div>
          <img src="/images/abb-support.jpg" />
        </div>
      ) : (
        'No support information.'
      )}
    </>
  );
};

export default SupportView;
